import { useState } from 'react';
import styled from '@emotion/styled';
import {
  Skeleton,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  useSingletonTabletMediaQuery,
} from '@sunrun/experience-ui-components';
import { DashboardWidgetContainer } from 'components/atoms/DashboardWidgetContainer';
import {
  DashboardLayoutItem,
  BLOCK_ROW_HEIGHT_PX,
  BLOCK_ROW_GAP_PX,
} from 'components/molecules/DashboardLayout';
import { TrainingAlertModal } from 'components/molecules/TrainingAlertModal';
import { formatDateMMDDYYYY } from 'helpers/formatHelpers';
import { useTrainings } from 'hooks/useTrainings';
import type { Training, TrainingAlert } from 'hooks/useTrainings';

type StatusDisplay = {
  label: string;
  color: string;
};

function getStatusDisplay(enrollmentStatus: string): StatusDisplay {
  switch (enrollmentStatus?.toLowerCase()) {
    case 'completed':
      return { label: 'Completed', color: tokens.SUCCESS_20 };
    case 'overdue':
      return { label: 'Past due', color: tokens.ERROR_40 };
    case 'in progress':
      return { label: 'In Progress', color: tokens.BRAND_MEDIUMBLUE };
    case 'enrolled':
      return { label: 'Not started', color: tokens.WARNING_40 };
    default:
      return { label: enrollmentStatus, color: tokens.BRAND_MEDIUMBLUE };
  }
}

type TrainingsWidgetProps = {
  trainings: Training[];
  isTrainingsLoading: boolean;
  trainingsError: Error | undefined;
  trainingAlert: TrainingAlert | undefined;
  isExpanded?: boolean;
};

const TrainingsWidget = ({
  trainings,
  isTrainingsLoading,
  trainingsError,
  trainingAlert,
  isExpanded = false,
}: TrainingsWidgetProps) => {
  const isMobile = useSingletonMobileMediaQuery();

  const showError =
    !isTrainingsLoading && !!trainingsError && trainings.length === 0;

  if (isTrainingsLoading) {
    return (
      <LoadingContainer>
        {[1, 2, 3].map((i) => (
          <StyledSkeleton key={i} />
        ))}
      </LoadingContainer>
    );
  }

  if (showError) {
    return (
      <StateContainer>
        <SvgIcon
          name={SvgNames.ErrorOutline}
          width={28}
          height={28}
          color={tokens.TINTS_ERROR_40}
        />
        <Typography size="small" align="center">
          Couldn&apos;t load trainings. Please try again later.
        </Typography>
      </StateContainer>
    );
  }

  if (trainings.length === 0) {
    return (
      <StateContainer>
        <Typography size="small">
          You have no training currently assigned.
        </Typography>
      </StateContainer>
    );
  }

  return (
    <TableContainer $isExpanded={isExpanded}>
      {!isMobile && (
        <HeaderRow>
          <TrainingCell>
            <HeaderLabel>Training</HeaderLabel>
          </TrainingCell>
          <DueDateCell>
            <HeaderLabel align="left">Due date</HeaderLabel>
          </DueDateCell>
          <StatusCell>
            <HeaderLabel align="left">Status</HeaderLabel>
          </StatusCell>
          <ArrowCell />
        </HeaderRow>
      )}

      <ScrollableBody $isExpanded={isExpanded}>
        {trainings?.map((training) => {
          const { label, color } = getStatusDisplay(training.enrollmentStatus);
          return (
            <DataRow
              showLastBorderBottom
              key={training.courseUniqueId}
              $isMobile={isMobile}
              onClick={() => window.open(training.courseUrl, '_blank')}
              role="button"
              tabIndex={0}
              aria-label={`Open ${training.courseName}`}>
              {isMobile ? (
                <MobileRow>
                  <MobileNameRow>
                    <CellText>{training.courseName}</CellText>
                    <SvgIcon
                      name={SvgNames.ChevronRight}
                      width={16}
                      height={16}
                      color={tokens.BRAND_HEROBLUE}
                    />
                  </MobileNameRow>
                  <MobileMetaRow>
                    <MobileMetaHalf>
                      <MobileMetaLabel>Due date</MobileMetaLabel>
                      <CellText>
                        {formatDateMMDDYYYY(training.enrollmentEndDate)}
                      </CellText>
                    </MobileMetaHalf>
                    <MobileMetaHalf>
                      <MobileMetaLabel>Status</MobileMetaLabel>
                      <StatusLabel $color={color}>
                        <StatusDot $color={color} />
                        <StatusText>{label}</StatusText>
                      </StatusLabel>
                    </MobileMetaHalf>
                  </MobileMetaRow>
                </MobileRow>
              ) : (
                <>
                  <TrainingCell>
                    <CellText>{training.courseName}</CellText>
                  </TrainingCell>
                  <DueDateCell>
                    <CellText align="right">
                      {formatDateMMDDYYYY(training.enrollmentEndDate)}
                    </CellText>
                  </DueDateCell>
                  <StatusCell>
                    <StatusLabel $color={color}>
                      <StatusDot $color={color} />
                      <StatusText>{label}</StatusText>
                    </StatusLabel>
                  </StatusCell>
                  <ArrowCell>
                    <SvgIcon
                      name={SvgNames.ChevronRight}
                      width={16}
                      height={16}
                      color={tokens.BRAND_HEROBLUE}
                    />
                  </ArrowCell>
                </>
              )}
            </DataRow>
          );
        })}
      </ScrollableBody>
      {trainingAlert?.alertLevel && trainingAlert.courseUrl && (
        <TrainingAlertModal
          alertLevel={trainingAlert.alertLevel}
          courseUrl={trainingAlert.courseUrl}
          dueDate={trainingAlert.enrollmentEndDate}
        />
      )}
    </TableContainer>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
`;

const StyledSkeleton = styled(Skeleton)`
  width: 100%;
  height: 40px;
  min-height: 40px;
`;

const StateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  text-align: center;
`;

const TableContainer = styled.div<{ $isExpanded?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  flex: 1;

  ${({ $isExpanded }) =>
    $isExpanded ? `overflow: visible;` : `overflow: hidden;`}
`;

const ScrollableBody = styled.div<{ $isExpanded?: boolean }>`
  flex: 1;
  min-height: 0;
  margin-bottom: 6px;
  overflow-y: ${({ $isExpanded }) => ($isExpanded ? 'visible' : 'auto')};
`;

const ROW_STYLES = `
  display: flex;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  min-height: 40px;
  padding: 0;
`;

const HeaderRow = styled.div`
  ${ROW_STYLES}
`;

const DataRow = styled.div<{
  $isMobile?: boolean;
  showLastBorderBottom?: boolean;
}>`
  ${ROW_STYLES}
  cursor: pointer;

  &:last-of-type {
    border-bottom: ${({ showLastBorderBottom }) =>
      showLastBorderBottom ? `1px solid ${tokens.TINTS_OFF_WHITE_40}` : 'none'};
  }

  &:hover {
    opacity: 0.85;
  }

  ${({ $isMobile }) =>
    $isMobile &&
    `
    flex-direction: column;
    align-items: stretch;
    padding: 0;
  `}
`;

const TrainingCell = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: 8px 12px;
`;

const DueDateCell = styled.div`
  flex: 0 0 110px;
  width: 110px;
  padding: 8px 12px;
  text-align: left;
`;

const StatusCell = styled.div`
  flex: 0 0 120px;
  width: 120px;
  padding: 8px 12px 8px 8px;
  text-align: left;
`;

const ArrowCell = styled.div`
  flex: 0 0 33px;
  width: 33px;
  padding: 8px 8px 8px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderLabel = styled.span<{ align?: 'left' | 'right' }>`
  display: block;
  text-align: ${({ align = 'left' }) => align};
  font-size: 12px;
  line-height: 18px;
  color: ${tokens.TINTS_HERO_BLUE_50};
`;

const CellText = styled(Typography)<{ align?: 'left' | 'right' }>`
  display: block;
  text-align: ${({ align = 'left' }) => align};
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: -0.28px;
  color: ${tokens.BRAND_HEROBLUE};
`;

const StatusText = styled(Typography)`
  font-size: 14px;
  line-height: 1.2;
  letter-spacing: -0.28px;
  font-weight: 500;
`;

const StatusLabel = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  color: ${({ $color }) => $color};

  & > * {
    color: inherit !important;
  }
`;

const StatusDot = styled.span<{ $color: string }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
  }
`;

/* ── Mobile-only elements ───────────────────────────────────────── */

const MobileRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MobileNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 4px;
  gap: 8px;
`;

const MobileMetaRow = styled.div`
  display: flex;
  padding: 4px 12px 12px;
  gap: 8px;
`;

const MobileMetaHalf = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const MobileMetaLabel = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: ${tokens.TINTS_OFF_WHITE_20};
  flex-shrink: 0;
`;

const DOCEBO_LEARNING_HOME_URL = 'https://sunrun.docebosaas.com/';

const COLLAPSED_BLOCK_ROWS = { mobile: 6, tablet: 6, desktop: 4 };
const HEADER_HEIGHT_PX = 52;
const ROW_HEIGHT_PX = 40;
const MOBILE_ROW_HEIGHT_PX = 80;
const MAX_EXPANDED_TRAININGS_ROWS = 20;
const MIN_TRAININGS_TO_EXPAND = 3;
// ArrowsInSimple's artwork doesn't fill its viewBox the same way
// ArrowsOutSimple's does, so it renders visibly smaller at the same size —
// bump it up to visually match the collapsed/expand icon pair.
const COLLAPSE_ICON_SIZE = 32;
const EXPAND_ICON_SIZE = 24;

/**
 * Rows needed to show every training without an internal scrollbar, capped so
 * a rep with a very long list can't blow out the dashboard layout (their
 * table keeps its own scroll in that case).
 */
function calculateExpandedBlockRows(
  trainingsCount: number,
  isMobile: boolean,
): number {
  const headerRowHeightPx = isMobile ? 0 : ROW_HEIGHT_PX;
  const rowHeightPx = isMobile ? MOBILE_ROW_HEIGHT_PX : ROW_HEIGHT_PX;
  const contentHeightPx =
    HEADER_HEIGHT_PX + headerRowHeightPx + trainingsCount * rowHeightPx;

  const blockRows = Math.ceil(
    (contentHeightPx + BLOCK_ROW_GAP_PX) /
      (BLOCK_ROW_HEIGHT_PX + BLOCK_ROW_GAP_PX),
  );

  const maxContentHeightPx =
    HEADER_HEIGHT_PX + MAX_EXPANDED_TRAININGS_ROWS * rowHeightPx;
  const maxExpandedRows = Math.ceil(
    (maxContentHeightPx + BLOCK_ROW_GAP_PX) /
      (BLOCK_ROW_HEIGHT_PX + BLOCK_ROW_GAP_PX),
  );

  return Math.min(blockRows, maxExpandedRows);
}

function getBlockRows(
  trainingsCount: number,
  isMobile: boolean,
  isExpanded: boolean,
  isDesktop = !isMobile,
) {
  if (isExpanded) {
    return calculateExpandedBlockRows(trainingsCount, isMobile);
  }

  if (isDesktop) {
    return COLLAPSED_BLOCK_ROWS.desktop;
  }

  return isMobile ? COLLAPSED_BLOCK_ROWS.mobile : COLLAPSED_BLOCK_ROWS.tablet;
}

type RunXLearningCardProps = {
  isExpanded: boolean;
  onToggleExpand: () => void;
  trainings: Training[];
  isTrainingsLoading: boolean;
  trainingsError: Error | undefined;
  trainingAlert: TrainingAlert | undefined;
};

const RunXLearningCard = ({
  isExpanded,
  onToggleExpand,
  trainings,
  isTrainingsLoading,
  trainingsError,
  trainingAlert,
}: RunXLearningCardProps) => {
  const canExpand = trainings.length > MIN_TRAININGS_TO_EXPAND;

  return (
    <DashboardWidgetContainer
      header="RunX Learning"
      preserveHeaderCase
      navigateTo={() => {
        window.open(DOCEBO_LEARNING_HOME_URL, '_blank');
      }}
      headerAction={
        canExpand
          ? {
              icon: isExpanded
                ? SvgNames.ArrowsInSimple
                : SvgNames.ArrowsOutSimple,
              iconSize: isExpanded ? COLLAPSE_ICON_SIZE : EXPAND_ICON_SIZE,
              onClick: onToggleExpand,
              ariaLabel: isExpanded ? 'Collapse' : 'Expand',
            }
          : undefined
      }>
      <TrainingsWidget
        trainings={trainings}
        isTrainingsLoading={isTrainingsLoading}
        trainingsError={trainingsError}
        trainingAlert={trainingAlert}
        isExpanded={isExpanded}
      />
    </DashboardWidgetContainer>
  );
};

type RunXLearningLayoutItemProps = {
  isMobile: boolean;
};

const RunXLearningLayoutItem = ({ isMobile }: RunXLearningLayoutItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isTablet = useSingletonTabletMediaQuery();
  const isDesktop = !isMobile && !isTablet;
  const { trainings, isTrainingsLoading, trainingsError, trainingAlert } =
    useTrainings();

  const blockRows = getBlockRows(
    trainings.length,
    isMobile,
    isExpanded,
    isDesktop,
  );

  return (
    <DashboardLayoutItem blockColumns={isMobile ? 1 : 2} blockRows={blockRows}>
      <RunXLearningCard
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((value) => !value)}
        trainings={trainings}
        isTrainingsLoading={isTrainingsLoading}
        trainingsError={trainingsError}
        trainingAlert={trainingAlert}
      />
    </DashboardLayoutItem>
  );
};

export {
  TrainingsWidget,
  RunXLearningCard,
  RunXLearningLayoutItem,
  calculateExpandedBlockRows,
  getBlockRows as getRunXLearningBlockRows,
};
